/**
 * トランスクリプト入力モーダル
 * テンプレート選択後、最初にマップを開いた時に表示される
 * ユーザーがトランスクリプトを入力できるモーダル
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, FileTextIcon, InfoIcon, Loader2Icon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

interface TranscriptInputModalProps {
  isOpen: boolean;
  onClose: (transcript?: string) => void;
  defaultTranscript?: string;
  mapName?: string;
}

const TranscriptInputModal: React.FC<TranscriptInputModalProps> = ({
  isOpen,
  onClose,
  defaultTranscript = '',
  mapName = 'マップ'
}) => {
  const [transcript, setTranscript] = useState<string>(defaultTranscript);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  
  // テキストエリアの変更を処理
  const handleTranscriptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTranscript(e.target.value);
  };
  
  // 保存して閉じる
  const handleSave = async () => {
    if (!transcript.trim()) {
      toast.warning('トランスクリプトが入力されていません', {
        description: '後からでも入力できます'
      });
      onClose();
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // トランスクリプトを保存して閉じる
      onClose(transcript);
      toast.success('トランスクリプトを保存しました', {
        description: 'これからマッピングを行えます'
      });
    } catch (error) {
      console.error('Error saving transcript:', error);
      toast.error('保存中にエラーが発生しました', {
        description: 'もう一度お試しください'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // 後で入力する（スキップ）
  const handleSkip = () => {
    toast.info('トランスクリプトは後からでも入力できます', {
      description: '右側のパネルから入力できます'
    });
    onClose();
  };
  
  // サンプルデータを使用
  const handleUseSample = () => {
    const sampleTranscript = `
インタビュアー: 今日はお時間をいただきありがとうございます。これまでのあなたの経験や、どのようなことに価値を感じているかについて伺っていきたいと思います。

学生: よろしくお願いします。

インタビュアー: 学生時代に特に力を入れて取り組んだことはありますか？

学生: 私は大学3年生のときにゼミのプロジェクトでチームリーダーを務めました。地域の高齢化問題に対する解決策を提案するプロジェクトで、メンバー8人を統括して約6ヶ月間活動しました。他のチームより2倍の時間をかけて地域の方々へのインタビューを実施し、延べ50人以上の高齢者の方から話を聞きました。

インタビュアー: 大変な取り組みだったと思いますが、具体的にどのようなことを行ったのですか？

学生: チームメンバーの意見がバラバラで進まない状況があったので、週に2回の定例ミーティングを設定し、毎回必ずアジェンダを事前に共有するようにしました。また、インタビューから得られた情報を整理するためのフレームワークを自分で考案して、データを構造化して分析しやすくしました。さらに、メンバーの得意分野を活かせるように役割分担を再設計して、全員が強みを発揮できる環境を整えました。
`;
    setTranscript(sampleTranscript);
    toast.info('サンプルデータをロードしました', {
      description: '内容を確認し、必要に応じて編集してください'
    });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <FileTextIcon className="h-5 w-5" />
            トランスクリプトの入力
          </DialogTitle>
          <DialogDescription>
            「{mapName}」のトランスクリプト（会話内容）を入力してください。これが分析の元データとなります。
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 my-2">
          <Textarea
            value={transcript}
            onChange={handleTranscriptChange}
            placeholder="会話やインタビューの内容をここに入力してください..."
            className="min-h-[300px] font-mono text-sm"
          />
          
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleUseSample}
              className="text-xs"
            >
              サンプルデータを使用
            </Button>
          </div>
        </div>
        
        <DialogFooter className="flex space-x-2 mt-4">
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={isSubmitting}
          >
            後で入力する
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                保存中...
              </>
            ) : (
              '保存して続ける'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TranscriptInputModal;